import React, { useState } from 'react';
import { XIcon, DownloadIcon, StarIcon, ExternalLinkIcon, MaximizeIcon, MinimizeIcon } from 'lucide-react';
import { SheetMusicItem } from './SheetMusicCard';
interface SheetMusicPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: SheetMusicItem;
  darkMode: boolean;
  onDownload?: (item: SheetMusicItem) => void;
  onToggleFavorite?: (id: string, isFavorite: boolean) => void;
}
export const SheetMusicPreviewModal: React.FC<SheetMusicPreviewModalProps> = ({
  isOpen,
  onClose,
  item,
  darkMode,
  onDownload,
  onToggleFavorite
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  if (!isOpen) return null;
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };
  const handleDownload = () => {
    if (onDownload) {
      onDownload(item);
    } else {
      // Default download behavior
      const resolvedUrl = item.firebaseUrl ?? item.pdfUrl;
      window.open(resolvedUrl, '_blank');
    }
  };
  return <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-10480">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center" data-id="element-10481">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity bg-black bg-opacity-75" onClick={onClose} aria-hidden="true" data-id="element-10482"></div>
        {/* Modal panel */}
        <div className={`inline-block w-full max-w-4xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform rounded-2xl shadow-xl ${darkMode ? 'bg-navy-800' : 'bg-white'} ${isFullscreen ? 'fixed inset-4 max-w-none my-0' : ''}`} onClick={e => e.stopPropagation()} data-id="element-10483">
          {/* Header */}
          <div className="flex items-center justify-between mb-4" data-id="element-10484">
            <div data-id="element-10485">
              <h3 className={`text-xl font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-10486">
                {item.title}
              </h3>
              <p className={`mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-10487">
                by {item.composer}
              </p>
            </div>
            <div className="flex items-center space-x-2" data-id="element-10488">
              {onToggleFavorite && <button onClick={() => onToggleFavorite(item.id, !item.isFavorite)} className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-navy-700' : 'hover:bg-gray-100'}`} aria-label={item.isFavorite ? 'Remove from favorites' : 'Add to favorites'} data-id="element-10489">
                  <StarIcon className={`h-5 w-5 ${item.isFavorite ? 'text-yellow-400 fill-current' : darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10490" />
                </button>}
              <button onClick={toggleFullscreen} className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-navy-700' : 'hover:bg-gray-100'}`} aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'} data-id="element-10491">
                {isFullscreen ? <MinimizeIcon className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10492" /> : <MaximizeIcon className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10493" />}
              </button>
              <button onClick={onClose} className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-navy-700' : 'hover:bg-gray-100'}`} aria-label="Close modal" data-id="element-10494">
                <XIcon className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10495" />
              </button>
            </div>
          </div>
          {/* PDF Viewer */}
          <div className={`w-full bg-gray-100 rounded-lg overflow-hidden ${isFullscreen ? 'h-[calc(100vh-180px)]' : 'h-[60vh]'}`} data-id="element-10496">
            <iframe src={`${item.firebaseUrl ?? item.pdfUrl}#toolbar=0&navpanes=0`} className="w-full h-full" title={`${item.title} by ${item.composer}`} data-id="element-10497" />
          </div>
          {/* Footer */}
          <div className="flex justify-between items-center mt-4" data-id="element-10498">
            <div className="flex flex-wrap gap-1.5" data-id="element-10499">
              {item.instruments.map((instrument, index) => <span key={index} className={`text-xs px-2 py-1 rounded-full ${darkMode ? 'bg-navy-900/50 text-gray-300' : 'bg-gray-100 text-gray-700'}`} data-id="element-10500">
                  {instrument}
                </span>)}
              <span className={`text-xs px-2 py-1 rounded-full ${darkMode ? 'bg-navy-900/50 text-gray-300' : 'bg-gray-100 text-gray-700'}`} data-id="element-10501">
                {item.era}
              </span>
              <span className={`text-xs px-2 py-1 rounded-full ${item.difficulty === 'Beginner' ? 'bg-green-500/80 text-white' : item.difficulty === 'Intermediate' ? 'bg-yellow-500/80 text-white' : 'bg-red-500/80 text-white'}`} data-id="element-10502">
                {item.difficulty}
              </span>
            </div>
            <div className="flex space-x-2" data-id="element-10503">
              <a href={item.firebaseUrl ?? item.pdfUrl} target="_blank" rel="noopener noreferrer" className={`py-2 px-3 rounded-lg transition-colors flex items-center text-sm ${darkMode ? 'bg-navy-900/50 hover:bg-navy-700 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'}`} data-id="element-10504">
                <ExternalLinkIcon className="h-4 w-4 mr-1.5" data-id="element-10505" />
                Open in New Tab
              </a>
              <button onClick={handleDownload} className="bg-deep-pink hover:bg-burgundy text-white py-2 px-4 rounded-lg transition-colors flex items-center text-sm" data-id="element-10506">
                <DownloadIcon className="h-4 w-4 mr-1.5" data-id="element-10507" />
                Download PDF
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
};